import { useState, useEffect, useRef, useCallback } from 'react';

// Durations in seconds — preserving values from original FocusMode.js
const DEFAULTS = {
  workDuration: 25 * 60,       // 25 min
  shortBreakDuration: 5 * 60,  // 5 min
  longBreakDuration: 15 * 60,  // 15 min
  sessionsBeforeLong: 4,
};

const STORAGE_KEY = 'bml.pomodoro.v1';

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage may be unavailable in private browsing
  }
}

// Synthesize a short 880Hz -> 660Hz beep via Web Audio API.
// The sounds directory does not exist, so no mp3 file is available.
// This creates an AudioContext only on demand (avoids autoplay policy issues).
function playCompletionBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.15);

    gain.gain.setValueAtTime(0.35, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.25);

    // Allow GC of the context after beep completes
    osc.addEventListener('ended', () => ctx.close());
  } catch {
    // Audio not supported — silent fallback
  }
}

function getDurationForMode(mode) {
  if (mode === 'work') return DEFAULTS.workDuration;
  if (mode === 'shortBreak') return DEFAULTS.shortBreakDuration;
  return DEFAULTS.longBreakDuration;
}

/**
 * usePomodoro
 * Owns all Pomodoro timer state. Persists to localStorage so a tab refresh
 * or backgrounding does not lose state.
 *
 * Timer tick strategy: setInterval at 250ms, but React state (remainingMs)
 * only updates when the second boundary changes. This prevents per-frame
 * re-renders while keeping the display accurate to the second.
 *
 * @param {{ onComplete?: (mode: string) => void }} options
 * @returns {{
 *   mode: 'work'|'shortBreak'|'longBreak',
 *   remainingMs: number,
 *   isRunning: boolean,
 *   cycleCount: number,
 *   totalWorkSecondsToday: number,
 *   durationMs: number,
 *   start: () => void,
 *   pause: () => void,
 *   reset: () => void,
 *   skip: () => void,
 * }}
 */
export function usePomodoro({ onComplete } = {}) {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Initialise from persisted state; if it was running, compute elapsed gap
  const [state, setState] = useState(() => {
    const saved = loadState();
    if (!saved) {
      return {
        mode: 'work',
        remainingMs: DEFAULTS.workDuration * 1000,
        isRunning: false,
        cycleCount: 0,
        totalWorkSecondsToday: 0,
        lastTickAt: null,
      };
    }

    let remainingMs = saved.remainingMs ?? DEFAULTS.workDuration * 1000;

    // If the timer was running when state was last saved, compute elapsed time
    if (saved.isRunning && saved.lastTickAt) {
      const elapsed = Date.now() - saved.lastTickAt;
      remainingMs = Math.max(0, remainingMs - elapsed);
    }

    return {
      mode: saved.mode ?? 'work',
      remainingMs,
      isRunning: saved.isRunning ?? false,
      cycleCount: saved.cycleCount ?? 0,
      totalWorkSecondsToday: saved.totalWorkSecondsToday ?? 0,
      lastTickAt: saved.isRunning ? Date.now() : null,
    };
  });

  // Ref-shadow of state for use inside setInterval callback without stale closure
  const stateRef = useRef(state);
  stateRef.current = state;

  const intervalRef = useRef(null);

  // Persist state to localStorage on every change
  useEffect(() => {
    saveState({
      mode: state.mode,
      remainingMs: state.remainingMs,
      isRunning: state.isRunning,
      cycleCount: state.cycleCount,
      totalWorkSecondsToday: state.totalWorkSecondsToday,
      lastTickAt: state.isRunning ? Date.now() : null,
    });
  }, [state]);

  // Tick every 250ms; only update React state when the displayed second changes
  useEffect(() => {
    if (!state.isRunning) {
      clearInterval(intervalRef.current);
      return;
    }

    let lastDisplayedSecond = Math.ceil(state.remainingMs / 1000);

    intervalRef.current = setInterval(() => {
      const s = stateRef.current;
      if (!s.isRunning) return;

      const elapsed = 250;
      const newRemainingMs = Math.max(0, s.remainingMs - elapsed);
      const newSecond = Math.ceil(newRemainingMs / 1000);

      if (newRemainingMs === 0) {
        // Block complete
        clearInterval(intervalRef.current);
        playCompletionBeep();

        const completedMode = s.mode;
        const newCycleCount = completedMode === 'work' ? s.cycleCount + 1 : s.cycleCount;
        const newTotalWork = completedMode === 'work'
          ? s.totalWorkSecondsToday + DEFAULTS.workDuration
          : s.totalWorkSecondsToday;

        let nextMode;
        if (completedMode === 'work') {
          nextMode = newCycleCount % DEFAULTS.sessionsBeforeLong === 0
            ? 'longBreak'
            : 'shortBreak';
        } else {
          nextMode = 'work';
        }

        setState({
          mode: nextMode,
          remainingMs: getDurationForMode(nextMode) * 1000,
          isRunning: false,
          cycleCount: newCycleCount,
          totalWorkSecondsToday: newTotalWork,
          lastTickAt: null,
        });

        if (onCompleteRef.current) {
          onCompleteRef.current(completedMode);
        }
        return;
      }

      // Only call setState when displayed second changes to avoid unnecessary renders
      if (newSecond !== lastDisplayedSecond) {
        lastDisplayedSecond = newSecond;
        setState((prev) => ({
          ...prev,
          remainingMs: newRemainingMs,
          lastTickAt: Date.now(),
        }));
      } else {
        // Update the ref directly so elapsed math stays accurate between renders
        stateRef.current = { ...s, remainingMs: newRemainingMs };
      }
    }, 250);

    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isRunning]);

  const start = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isRunning: true,
      lastTickAt: Date.now(),
    }));
  }, []);

  const pause = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isRunning: false,
      lastTickAt: null,
    }));
  }, []);

  const reset = useCallback(() => {
    setState((prev) => ({
      ...prev,
      remainingMs: getDurationForMode(prev.mode) * 1000,
      isRunning: false,
      lastTickAt: null,
    }));
  }, []);

  const skip = useCallback(() => {
    setState((prev) => {
      let nextMode;
      if (prev.mode === 'work') {
        const nextCycle = prev.cycleCount + 1;
        nextMode = nextCycle % DEFAULTS.sessionsBeforeLong === 0 ? 'longBreak' : 'shortBreak';
        return {
          ...prev,
          mode: nextMode,
          remainingMs: getDurationForMode(nextMode) * 1000,
          isRunning: false,
          cycleCount: nextCycle,
          lastTickAt: null,
        };
      }
      nextMode = 'work';
      return {
        ...prev,
        mode: nextMode,
        remainingMs: getDurationForMode(nextMode) * 1000,
        isRunning: false,
        lastTickAt: null,
      };
    });
  }, []);

  return {
    mode: state.mode,
    remainingMs: state.remainingMs,
    isRunning: state.isRunning,
    cycleCount: state.cycleCount,
    totalWorkSecondsToday: state.totalWorkSecondsToday,
    durationMs: getDurationForMode(state.mode) * 1000,
    start,
    pause,
    reset,
    skip,
  };
}
